import './EventsFiltersPanel.scss';

import { Button, Chip } from '@trackman/web-shared-components';
import { TrackmanEventFiltersKeys, TrackmanEventFiltersType, TrackmanEventModel } from 'types/trackmanEvents';

import { ReactComponent as CloseIcon } from 'images/icons/Close.svg';
import Paragraph from 'components/TextComps/Paragraph';
import Separator from 'components/Separator/Separator';
import Subtitle from 'components/TextComps/Subtitle';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

type FilterOption = {
  label: string;
  value: string | boolean;
};

type FilterConfig = {
  key: string;
  label: string;
  options: FilterOption[];
};

type Props = {
  filtersConfig: FilterConfig[];
  eventsList: TrackmanEventModel[];
  selectedFilters: TrackmanEventFiltersKeys;
  onFilterClick: (isSelected: boolean, label: string, filter: TrackmanEventFiltersType) => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
  isFiltersVisible: boolean;
  showFilters: (value: boolean) => void;
};

export const EventsFiltersPanel = observer(
  ({ filtersConfig, eventsList, selectedFilters, onFilterClick, onClearFilters, onApplyFilters, isFiltersVisible, showFilters }: Props) => {
    const { t } = useTranslation();

    return (
      <div className={`events-filter-panel ${isFiltersVisible ? 'filters-opened' : ''}`}>
        <div className='filter-panel-heading'>
          <Subtitle>
            <>{t('Filter', { ns: 'trackmanEvents' })}</>
          </Subtitle>
          <button className='close-btn' onClick={() => showFilters(false)}>
            <CloseIcon />
          </button>
        </div>
        <Separator />
        <div className='filter-panel-body'>
          {filtersConfig.map((filter, index) => (
            <div key={filter.key} className='filter-options-wrapper'>
              <div className='filter-option-label'>
                <Paragraph>
                  <>{filter.label}</>
                </Paragraph>
              </div>
              <div className='filter-options'>
                {filter.options.map((option) => {
                  const isSelected = selectedFilters[filter.key]?.some((item) => item.label === option.label);
                  return (
                    <Chip
                      key={option.label}
                      className={isSelected ? 'selected-filter' : ''}
                      clickable
                      label={option.label}
                      onClick={() => onFilterClick(isSelected, filter.key, { label: option.label, value: option.value })}
                    />
                  );
                })}
              </div>
              {index < filtersConfig.length - 1 ? <Separator /> : null}
            </div>
          ))}
        </div>
        <div className='filter-panel-footer'>
          <Button
            className='styled-button tertiary'
            onClick={() => (Object.keys(selectedFilters).length ? onClearFilters() : showFilters(false))}
          >
            <>{Object.keys(selectedFilters).length ? t('ClearAll', { ns: 'trackmanEvents' }) : t('Cancel', { ns: 'general' })}</>
          </Button>
          <Button className='styled-button primary' onClick={onApplyFilters}>
            <>{t('Apply', { ns: 'trackmanEvents' })}</>
          </Button>
        </div>
      </div>
    );
  }
);

export default EventsFiltersPanel;
